import { createEntityAdapter, EntityAdapter, EntityState } from '@ngrx/entity';
import { createFeature, createReducer, createSelector, on } from '@ngrx/store';
import { DateTime } from 'luxon';
import * as fromGenerated from '../../_generated';
import { ChatNavigationItem } from '../chats.interfaces';
// Import the concrete actions files, not the shared barrel: the barrel pulls
// the selector modules, which depend back on this store module — a cycle
// that breaks under jest's CJS interop (same precedent as chats.selectors.ts).
import { DocumentViewerActions } from '../shared/actions/document-viewer.actions';
import { ImageOverlayActions } from '../shared/actions/image-overlay.actions';
import {
  ChatActions,
  ChatModalActions,
  ChatsActions,
  ChatWithMessagesActions,
} from './chats.actions';
import {
  ActiveRenameRequest,
  clampUpdatedAtToNewest,
  confirmRenameIfCurrent,
  omitTitleWhileRenamePending,
  revertRenameIfCurrent,
  setRenameRequest,
  sortChatsByUpdatedAtComparer,
  sortMessagesByCreatedAt,
} from './chats.store.helper';

export const DOCUMENT_CREATED_BY_USER_TRANSIENT_STATE =
  'document-created-by-user-transient-state';

export type ChatViewWithRequiredTitle = Omit<fromGenerated.ChatView, 'title'> &
  Required<Pick<fromGenerated.ChatView, 'title'>>;
type DocumentViewWithError = fromGenerated.DocumentView & {
  error?: string | undefined;
};

const isChatViewWithRequiredTitle = (
  chat: fromGenerated.ChatView,
): chat is ChatViewWithRequiredTitle => {
  return !!chat.title;
};

export const chatsFeatureKey = 'chats';

/**
 * Which image the full-screen overlay viewer is showing.
 * `chatMessage` = a sent image; `composer` = a staged attachment. Both are
 * keyed by the Document uuid and resolved the same way by the smart component
 * (cached preview blob first, presigned fallback).
 */
export type ImageOverlaySource =
  | { kind: 'chatMessage'; documentUuid: string; filename: string }
  | { kind: 'composer'; uuid: string; filename: string };

export interface ImageOverlayState {
  isOpen: boolean;
  source: ImageOverlaySource | null;
}

export interface ChatWithMessages extends fromGenerated.ChatView {
  messages?: (fromGenerated.ChatMessageView & {
    errorDismissed?: boolean;
  })[];
  documents?: DocumentViewWithError[];
}

export interface ChatsState extends EntityState<ChatWithMessages> {
  selectedChatId: number | null;
  selectedChatMessageId: number | null;
  selectedDocumentId: string | null;
  isLoading: boolean;
  loaded: boolean;
  error: string | null;
  isNavigationMinimized: boolean;
  isAssistantProfileOpened: boolean;
  isCitationsLoading: boolean;
  isLastMessageAnimating: boolean;
  imageOverlay: ImageOverlayState;
  activeRenameRequests: Record<number, ActiveRenameRequest>;
}

export interface ChatModalState {
  isDeleteChatModalOpened: boolean;
  isRenameChatModalOpened: boolean;
}

const entityAdapter: EntityAdapter<ChatWithMessages> =
  createEntityAdapter<ChatWithMessages>({
    selectId: (view) => view.id,
    sortComparer: sortChatsByUpdatedAtComparer,
  });

export const initialChatsState: ChatsState & ChatModalState =
  entityAdapter.getInitialState({
    selectedChatId: null,
    selectedChatMessageId: null,
    selectedDocumentId: null,
    isLoading: false,
    loaded: false,
    error: null,
    isNavigationMinimized: false,
    isDeleteChatModalOpened: false,
    isRenameChatModalOpened: false,
    isAssistantProfileOpened: false,
    isCitationsLoading: false,
    isLastMessageAnimating: false,
    imageOverlay: { isOpen: false, source: null },
    activeRenameRequests: {},
  });

export const chatsFeature = createFeature({
  name: chatsFeatureKey,
  reducer: createReducer(
    initialChatsState,
    on(ChatModalActions.openDeleteChatModal, (state) => ({
      ...state,
      isDeleteChatModalOpened: true,
    })),
    on(ChatModalActions.closeDeleteChatModal, (state) => ({
      ...state,
      isDeleteChatModalOpened: false,
    })),
    on(ChatModalActions.openRenameChatModal, (state) => ({
      ...state,
      isRenameChatModalOpened: true,
    })),
    on(ChatModalActions.closeRenameChatModal, (state) => ({
      ...state,
      isRenameChatModalOpened: false,
    })),
    on(ChatActions.selectChat, (state, { id }) => {
      const chat = state.entities[id];
      const hasAssistant = chat?.assistantId != null;
      return {
        ...state,
        selectedChatId: id,
        selectedChatMessageId: null,
        selectedDocumentId: null,
        error: null,
        isLastMessageAnimating: false,
        // Close assistant profile when navigating to a chat without an assistant
        ...(hasAssistant ? {} : { isAssistantProfileOpened: false }),
      };
    }),
    on(ChatActions.selectEmptyChat, (state) => {
      const chatId = Object.values(state.entities).find(
        (chat) => chat?._count.messages === 0,
      )?.id;

      if (!chatId) {
        return state;
      }

      const chat = state.entities[chatId];
      const hasAssistant = chat?.assistantId != null;

      return entityAdapter.updateOne(
        { id: chatId, changes: { assistantId: null } },
        {
          ...state,
          selectedChatId: chatId,
          selectedChatMessageId: null,
          selectedDocumentId: null,
          error: null,
          isLastMessageAnimating: false,
          ...(hasAssistant ? {} : { isAssistantProfileOpened: false }),
        },
      );
    }),
    on(ChatActions.selectEmptyChatWithAssistant, (state, { assistantUuid }) => {
      const chatId = Object.values(state.entities).find(
        (chat) => chat?._count.messages === 0,
      )?.id;

      if (!chatId) {
        return state;
      }

      return entityAdapter.updateOne(
        { id: chatId, changes: { assistantId: assistantUuid } },
        {
          ...state,
          selectedChatId: chatId,
          selectedChatMessageId: null,
          selectedDocumentId: null,
          error: null,
          isLastMessageAnimating: false,
        },
      );
    }),
    on(ChatActions.unselectChat, (state) => ({
      ...state,
      selectedChatId: null,
      selectedChatMessageId: null,
      selectedDocumentId: null,
      error: null,
      isLastMessageAnimating: false,
    })),
    on(ChatsActions.loadChatsSuccess, (state, { chats }) => ({
      ...entityAdapter.upsertMany(chats, { ...state, loaded: true }),
    })),
    on(ChatsActions.loadChatsForAssistantSuccess, (state, { chats }) => ({
      ...entityAdapter.upsertMany(chats, state),
    })),
    on(
      ChatsActions.loadChatsFailure,
      ChatsActions.loadChatsForAssistantFailure,
      (state, { error }) => ({
        ...state,
        error,
      }),
    ),
    on(ChatsActions.deleteChat, (state, { id }) =>
      entityAdapter.removeOne(id, state),
    ),
    on(ChatsActions.deleteChatSuccess, (state, { id }) => ({
      ...state,
      ...(state.selectedChatId === id
        ? { isAssistantProfileOpened: false }
        : {}),
    })),
    on(ChatsActions.deleteChatFailure, (state, { error }) => ({
      ...state,
      error,
    })),
    on(ChatsActions.renameChat, (state, { id, title, requestId, renamedAt }) =>
      setRenameRequest(entityAdapter, state, id, title, requestId, renamedAt),
    ),
    on(ChatsActions.renameChatSuccess, (state, { id, title, requestId }) =>
      confirmRenameIfCurrent(entityAdapter, state, id, title, requestId),
    ),
    on(
      ChatsActions.renameChatFailure,
      (state, { id, previousTitle, requestId }) =>
        revertRenameIfCurrent(
          entityAdapter,
          state,
          id,
          previousTitle,
          requestId,
        ),
    ),
    on(
      ChatWithMessagesActions.loadChatWithMessages,
      (state, { withoutIsLoading }) => ({
        ...state,
        isLoading: withoutIsLoading ? false : true,
      }),
    ),
    on(
      ChatWithMessagesActions.loadChatWithMessagesSuccess,
      (state, { chatWithMessages }) => {
        const chat = state.entities[chatWithMessages.id];

        return entityAdapter.upsertOne(
          {
            ...(chat ? { ...chat } : {}),
            ...chatWithMessages,
            messages: sortMessagesByCreatedAt(
              chatWithMessages.messages.map((message) => ({
                ...(chat?.messages?.find((m) => m.id === message.id) ?? {}),
                ...message,
              })),
            ),
          },
          {
            ...state,
            isLoading: false,
          },
        );
      },
    ),
    on(
      ChatWithMessagesActions.loadChatWithMessagesFailure,
      (state, { error }) => ({
        ...state,
        error,
        isLoading: false,
      }),
    ),
    on(
      ChatWithMessagesActions.addChatMessage,
      (state, { content, assistant, documents }) => {
        const selectedChatId = state.selectedChatId;

        if (selectedChatId !== null) {
          const currentChat = state.entities[selectedChatId];

          if (currentChat) {
            const messages = currentChat.messages?.length
              ? [...currentChat.messages]
              : [];
            const currentTime = DateTime.utc();

            if (assistant) {
              const previousTime = currentTime.minus({
                second: 1,
              });
              messages.push({
                id: crypto.getRandomValues(new Uint32Array(1))[0],
                content: assistant.introMessage ?? assistant.description,
                isFromUser: false,
                chatId: currentChat.id,
                documents: [],
                documentsChunks: [],
                documentsCitations: [],
                createdAt: previousTime.toISO(),
                updatedAt: previousTime.toISO(),
                error: null,
                errorDismissed: false,
              });
            }

            messages.push({
              id: crypto.getRandomValues(new Uint32Array(1))[0],
              content,
              isFromUser: true,
              chatId: currentChat.id,
              documents: documents ?? [],
              documentsChunks: [],
              documentsCitations: [],
              createdAt: currentTime.toISO(),
              updatedAt: currentTime.toISO(),
              error: null,
              errorDismissed: false,
            });

            return entityAdapter.updateOne(
              {
                id: currentChat.id,
                changes: {
                  updatedAt: currentTime.toISO(),
                  ...(assistant ? { assistantId: assistant.uuid } : {}),
                  _count: {
                    ...currentChat._count,
                    messages: messages.length,
                  },
                  messages,
                },
              },
              state,
            );
          }
        }

        return state;
      },
    ),
    on(
      ChatWithMessagesActions.addChatMessageFailure,
      (state, { chatId, error }) => {
        const messages = state.entities[chatId]?.messages;

        if (!messages?.length) {
          return state;
        }

        const newMessages = [...messages];
        const lastMessage = newMessages.pop();

        if (lastMessage) {
          newMessages.push({
            ...lastMessage,
            error,
            errorDismissed: false,
          });
        }

        return entityAdapter.updateOne(
          {
            id: chatId,
            changes: { messages: newMessages },
          },
          state,
        );
      },
    ),
    on(
      ChatWithMessagesActions.addChatMessageSuccess,
      (state, { chatWithMessages }) =>
        entityAdapter.updateOne(
          {
            id: chatWithMessages.id,
            changes: { documents: [] },
          },
          state,
        ),
    ),
    on(
      ChatWithMessagesActions.dismissLastChatMessageError,
      (state, { chatId }) => {
        const messages = state.entities[chatId]?.messages;

        if (!messages?.length) {
          return state;
        }

        const newMessages = [...messages];
        const lastMessage = newMessages.pop();

        if (lastMessage) {
          newMessages.push({
            ...lastMessage,
            errorDismissed: true,
          });
        }

        return entityAdapter.updateOne(
          {
            id: chatId,
            changes: { messages: newMessages },
          },
          state,
        );
      },
    ),
    on(
      ChatWithMessagesActions.receivedChatWithMessagesFromSocketSuccess,
      (state, { chatWithMessages: { id, ...chatWithMessages } }) =>
        entityAdapter.updateOne(
          {
            id,
            changes: clampUpdatedAtToNewest(
              state.entities[id]?.updatedAt,
              omitTitleWhileRenamePending(state, id, {
                ...chatWithMessages,
                messages: sortMessagesByCreatedAt(
                  chatWithMessages.messages.map((message) => ({
                    ...message,
                    errorDismissed: false,
                  })),
                ),
              }),
            ),
          },
          { ...state },
        ),
    ),
    on(
      ChatWithMessagesActions.receivedChatWithMessagesFromSocketFailure,
      (state, { chatWithMessages }) =>
        entityAdapter.upsertOne(
          {
            ...chatWithMessages,
            // Upsert needs a full entity, so instead of omitting the title we
            // keep the store's optimistic one while a rename is in flight
            // (same rule as omitTitleWhileRenamePending).
            ...(state.activeRenameRequests[chatWithMessages.id]
              ? {
                  title:
                    state.entities[chatWithMessages.id]?.title ??
                    chatWithMessages.title,
                }
              : {}),
            ...clampUpdatedAtToNewest(
              state.entities[chatWithMessages.id]?.updatedAt,
              { updatedAt: chatWithMessages.updatedAt },
            ),
            messages: sortMessagesByCreatedAt(
              chatWithMessages.messages.map((message) => ({
                ...message,
                errorDismissed: false,
              })),
            ),
          },
          { ...state },
        ),
    ),
    on(
      ChatWithMessagesActions.requestQuickAction,
      (state, { chatId, quickActionMessage }) => {
        const chat = state.entities[chatId];

        if (chat) {
          const currentTime = DateTime.utc();
          const messages = chat.messages?.length ? [...chat.messages] : [];

          messages.push({
            id: crypto.getRandomValues(new Uint32Array(1))[0],
            content: quickActionMessage,
            isFromUser: true,
            chatId: chat.id,
            documents: [],
            documentsChunks: [],
            documentsCitations: [],
            createdAt: currentTime.toISO(),
            updatedAt: currentTime.toISO(),
            error: null,
            errorDismissed: false,
          });

          return entityAdapter.updateOne(
            {
              id: chat.id,
              changes: {
                updatedAt: currentTime.toISO(),
                _count: {
                  ...chat._count,
                  messages: messages.length,
                },
                messages,
              },
            },
            state,
          );
        }
        return state;
      },
    ),
    on(
      ChatWithMessagesActions.selectDocumentInChatMessage,
      (state, { chatMessageId, documentUuid }) => ({
        ...state,
        selectedChatMessageId: chatMessageId,
        selectedDocumentId: documentUuid,
        error: null,
      }),
    ),
    on(ChatActions.selectDocument, (state, { documentUuid }) => ({
      ...state,
      selectedDocumentId: documentUuid,
      error: null,
    })),
    on(ChatWithMessagesActions.unselectDocumentInChatMessage, (state) => ({
      ...state,
      selectedChatMessageId: null,
      selectedDocumentId: null,
    })),
    on(ChatActions.maximizeNavigation, (state) => ({
      ...state,
      isNavigationMinimized: false,
    })),
    on(ChatActions.minimizeNavigation, (state) => ({
      ...state,
      isNavigationMinimized: true,
    })),
    on(
      ChatWithMessagesActions.receivedDocumentCitationsFromSocketSuccess,
      (state, { chatId, chatMessageId, documentCitations }) => {
        const chat = state.entities[chatId];

        if (!chat?.messages?.length) {
          return state;
        }

        if (chat.messages.some((message) => message.id === chatMessageId)) {
          const now = DateTime.utc();

          const updatedMessages = chat.messages.map((message) =>
            message.id === chatMessageId
              ? {
                  ...message,
                  updatedAt: now.toISO(),
                  documentsCitations: [
                    ...message.documentsCitations,
                    ...documentCitations.filter(
                      (newCitation) =>
                        !message.documentsCitations.some(
                          (existingCitation) =>
                            existingCitation.uuid === newCitation.uuid,
                        ),
                    ),
                  ],
                }
              : message,
          );

          return entityAdapter.updateOne(
            {
              id: chatId,
              changes: {
                updatedAt: now.toISO(),
                messages: updatedMessages,
              },
            },
            { ...state, isCitationsLoading: false, error: null },
          );
        }

        return state;
      },
    ),
    on(ChatWithMessagesActions.requestDocumentCitations, (state) =>
      state.selectedChatId
        ? {
            ...state,
            isCitationsLoading: true,
          }
        : state,
    ),
    on(
      ChatWithMessagesActions.requestDocumentCitationsFailure,
      (state, { error }) => ({
        ...state,
        error,
        isCitationsLoading: false,
      }),
    ),
    on(ChatWithMessagesActions.setCitationsLoading, (state, { isLoading }) => ({
      ...state,
      isCitationsLoading: isLoading,
    })),
    on(ChatsActions.deleteManyChatsSuccess, (state, { ids }) =>
      entityAdapter.removeMany(ids, state),
    ),
    on(
      ChatWithMessagesActions.addDocuments,
      (state, { transactionId, chatId, files, documentRole }) => {
        const chat = state.entities[chatId];

        if (!chat) {
          return state;
        }

        const now = DateTime.utc().toISO();
        const documents: fromGenerated.DocumentView[] = [
          ...(chat?.documents ?? []),
          ...files.map((file) => ({
            transactionId,
            uuid: file.uuid,
            filename: file.name,
            extension: file.extension,
            size: file.size,
            repositoryKey: null,
            status: 'processing' as const,
            direction: 'input' as const,
            role: documentRole ?? 'regular',
            createdBy: DOCUMENT_CREATED_BY_USER_TRANSIENT_STATE,
            createdAt: now,
            deletedAt: null,
            updatedAt: now,
          })),
        ];

        return entityAdapter.updateOne(
          { id: chatId, changes: { documents } },
          state,
        );
      },
    ),
    on(
      ChatWithMessagesActions.addDocumentsSuccess,
      (state, { chatId, documents }) => {
        const existingDocuments = state.entities[chatId]?.documents;

        if (!existingDocuments) {
          return state;
        }

        return entityAdapter.updateOne(
          {
            id: chatId,
            changes: {
              documents: existingDocuments.map((doc) => {
                const updatedDocument = documents.find(
                  (newDoc) => newDoc.uuid === doc.uuid,
                );
                return updatedDocument ? { ...doc, ...updatedDocument } : doc;
              }),
            },
          },
          state,
        );
      },
    ),
    on(
      ChatWithMessagesActions.addDocumentsFailure,
      (state, { chatId, documentUuids, error }) => {
        const existingDocuments = state.entities[chatId]?.documents;

        if (!existingDocuments) {
          return state;
        }

        return entityAdapter.updateOne(
          {
            id: chatId,
            changes: {
              documents: existingDocuments.map((doc) =>
                documentUuids.includes(doc.uuid)
                  ? { ...doc, status: 'failed', error }
                  : doc,
              ),
            },
          },
          state,
        );
      },
    ),
    on(ChatWithMessagesActions.removeDocuments, (state, { chatId }) =>
      entityAdapter.updateOne(
        {
          id: chatId,
          changes: { documents: [] },
        },
        state,
      ),
    ),
    on(
      ChatWithMessagesActions.removeDocument,
      (state, { chatId, documentUuid }) =>
        entityAdapter.updateOne(
          {
            id: chatId,
            changes: {
              documents:
                state.entities[chatId]?.documents?.filter(
                  (doc) => doc.uuid !== documentUuid,
                ) ?? [],
            },
          },
          state,
        ),
    ),
    on(
      ChatWithMessagesActions.receivedDocumentStatusUpdatedFromSocketSuccess,
      (state, { chatUuid, documentId, documentStatus }) => {
        const chat = Object.values(state.entities).find(
          (chat) => chat?.uuid === chatUuid,
        );
        if (!chat) {
          return state;
        }

        // Check if the document exists in chat.documents
        const documentInChat = chat.documents?.find(
          (doc) => doc.uuid === documentId,
        );

        // Check if the document exists in any message
        const messageWithDocument = chat.messages?.find((message) =>
          message.documents?.some((doc) => doc.uuid === documentId),
        );

        // If document not found in either place, return unchanged state
        if (!documentInChat && !messageWithDocument) {
          return state;
        }

        const changes: Partial<ChatWithMessages> = {};

        // Update document status in chat.documents if found
        if (documentInChat && chat.documents) {
          changes.documents = chat.documents.map((doc) =>
            doc.uuid === documentId ? { ...doc, status: documentStatus } : doc,
          );
        }

        // Update document status in messages[].documents if found
        if (messageWithDocument && chat.messages) {
          changes.messages = chat.messages.map((message) => {
            if (!message.documents?.some((doc) => doc.uuid === documentId)) {
              return message;
            }
            return {
              ...message,
              documents: message.documents.map((doc) =>
                doc.uuid === documentId
                  ? { ...doc, status: documentStatus }
                  : doc,
              ),
            };
          });
        }

        return entityAdapter.updateOne(
          {
            id: chat.id,
            changes,
          },
          state,
        );
      },
    ),
    on(ChatActions.openAssistantProfile, (state) => ({
      ...state,
      isAssistantProfileOpened: true,
    })),
    on(ChatActions.closeAssistantProfile, (state) => ({
      ...state,
      isAssistantProfileOpened: false,
    })),
    on(ChatActions.updateChatAssistant, (state, { chatId, assistantUuid }) => {
      return entityAdapter.updateOne(
        {
          id: chatId,
          changes: { assistantId: assistantUuid ?? null },
        },
        {
          ...state,
          ...(assistantUuid ? {} : { isAssistantProfileOpened: false }),
        },
      );
    }),
    on(ChatActions.setLastMessageAnimating, (state, { isAnimating }) => ({
      ...state,
      isLastMessageAnimating: isAnimating,
    })),
    /**
     * Handle document viewer actions
     */
    on(
      DocumentViewerActions.openDocumentFromChatMessage,
      (state, { chatMessageId, documentUuid }) => ({
        ...state,
        selectedChatMessageId: chatMessageId,
        selectedDocumentId: documentUuid,
        isAssistantProfileOpened: false,
      }),
    ),
    on(
      DocumentViewerActions.openDocumentFromAssistantProfile,
      (state, { documentUuid, chatMessageId }) => ({
        ...state,
        selectedDocumentId: documentUuid,
        selectedChatMessageId: chatMessageId,
      }),
    ),
    on(DocumentViewerActions.closeDocumentViewer, (state) => ({
      ...state,
      selectedChatMessageId: null,
      selectedDocumentId: null,
    })),
    /**
     * Handle image overlay viewer actions. Additive slice —
     * independent of the doc-viewer's selected-document transitions above.
     */
    on(
      ImageOverlayActions.openImageFromChatMessage,
      (state, { documentUuid, filename }) => ({
        ...state,
        imageOverlay: {
          isOpen: true,
          source: { kind: 'chatMessage', documentUuid, filename },
        },
      }),
    ),
    on(
      ImageOverlayActions.openImageFromComposer,
      (state, { uuid, filename }) => ({
        ...state,
        imageOverlay: {
          isOpen: true,
          source: { kind: 'composer', uuid, filename },
        },
      }),
    ),
    on(ImageOverlayActions.closeImageOverlay, (state) => ({
      ...state,
      imageOverlay: { isOpen: false, source: null },
    })),
    // Close the overlay on chat navigation, mirroring the doc-viewer reset
    // (these actions already null `selectedDocumentId`). A separate `on` for
    // the same actions composes with their handlers above.
    on(
      ChatActions.selectChat,
      ChatActions.selectEmptyChat,
      ChatActions.selectEmptyChatWithAssistant,
      ChatActions.unselectChat,
      (state) => ({
        ...state,
        imageOverlay: { isOpen: false, source: null },
      }),
    ),
    on(
      DocumentViewerActions.closeDocumentViewerAndOpenAssistantProfile,
      (state) => ({
        ...state,
        selectedChatMessageId: null,
        selectedDocumentId: null,
        isAssistantProfileOpened: true,
      }),
    ),
    on(
      ChatActions.toggleAssistantProfile,
      DocumentViewerActions.toggleAssistantProfileAndConversationViewMode,
      (state) => ({
        ...state,
        ...(state.selectedChatMessageId
          ? { isAssistantProfileOpened: !state.isAssistantProfileOpened }
          : {
              selectedDocumentId: null,
              selectedChatMessageId: null,
              isAssistantProfileOpened: !state.isAssistantProfileOpened,
            }),
      }),
    ),
  ),
  extraSelectors: ({
    selectChatsState,
    selectSelectedChatId,
    selectSelectedChatMessageId,
    selectSelectedDocumentId,
  }) => {
    const entitySelectors = entityAdapter.getSelectors(selectChatsState);

    const selectSelectedChatMessage = createSelector(
      selectSelectedChatId,
      selectSelectedChatMessageId,
      entitySelectors.selectEntities,
      (selectedChatId, selectedChatMessageId, entities) =>
        selectedChatId && selectedChatMessageId
          ? entities[selectedChatId]?.messages?.find(
              (message) => message.id === selectedChatMessageId,
            )
          : null,
    );

    return {
      ...entitySelectors,
      selectChatNavigationItems: createSelector(
        selectSelectedChatId,
        entitySelectors.selectAll,
        (selectedChatId, chats) =>
          /**
           * We are filtering out chats without a title because we don't want to
           * show them in the navigation. Note that we should only have 1 chat
           * per user that doesn't have a title, which is the "Home Chat" up until
           * a message has been added to it.
           */
          chats
            .filter((chat) => chat._count.messages)
            .filter(isChatViewWithRequiredTitle)
            .map<ChatNavigationItem>((chat) => ({
              id: chat.id,
              title: chat.title,
              selected: chat.id === selectedChatId,
              date: DateTime.fromISO(chat.updatedAt).toLocal(),
              ...(chat.assistantId ? { assistantId: chat.assistantId } : {}),
            })),
      ),
      selectSelectedChat: createSelector(
        selectSelectedChatId,
        entitySelectors.selectEntities,
        (selectedChatId, entities) => {
          const selectedChat = selectedChatId ? entities[selectedChatId] : null;

          if (!selectedChat) {
            return null;
          }

          const lastMessage =
            selectedChat.messages?.[selectedChat.messages.length - 1];

          if (lastMessage?.isFromUser && !lastMessage.error) {
            return { ...selectedChat, isThinking: true };
          }

          return { ...selectedChat, isThinking: false };
        },
      ),
      selectChatById: (chatId: number) =>
        createSelector(
          entitySelectors.selectEntities,
          (entities) => entities[chatId] ?? null,
        ),
      selectSelectedDocumentFromChatMessage: createSelector(
        selectSelectedChatMessage,
        selectSelectedDocumentId,
        (selectedChatMessage, selectedDocumentId) => {
          if (!selectedChatMessage || !selectedDocumentId) {
            return null;
          }

          const documentAttachedToUserMessage =
            selectedChatMessage.documents.find(
              (document) => document.uuid === selectedDocumentId,
            );

          if (!documentAttachedToUserMessage) {
            return null;
          }

          const documentChunksForSelectedDocument =
            selectedChatMessage.documentsChunks.filter(
              ({ documentId }) => documentId === selectedDocumentId,
            );

          const documentCitationsForSelectedDocument =
            selectedChatMessage.documentsCitations.filter(
              ({ documentId }) => documentId === selectedDocumentId,
            );

          return {
            document: documentAttachedToUserMessage,
            documentChunks: documentChunksForSelectedDocument,
            documentCitations: documentCitationsForSelectedDocument,
          };
        },
      ),
      selectSelectedDocumentInChatMessage: createSelector(
        selectSelectedChatMessageId,
        selectSelectedDocumentId,
        (selectedChatMessageId, selectedDocumentId) => ({
          chatMessageId: selectedChatMessageId,
          documentUuid: selectedDocumentId,
        }),
      ),
      selectHasAnEmptyChat: createSelector(entitySelectors.selectAll, (chats) =>
        chats.some((chat) => chat._count.messages === 0),
      ),
    };
  },
});
